/**
 * The sidebar control, in its two states, as it is being written.
 *
 * It is the same control: off it invites you in with `enterCta`, on it offers the way back with
 * `exitCta`. Seen side by side, a label that only makes sense in one of the states is obvious;
 * in a form they are two fields three rows apart.
 *
 * It is drawn as a **button at the top of the sidebar** — primary while it invites, tertiary while
 * it offers the way back — and not as a menu row, because that is where it is moving to. Drawn
 * here rather than imported: the entry point belongs to each dashboard's sidebar and is not a
 * `gl-ui` component. The two modals are previewed with the real thing instead.
 *
 * **It draws what was chosen, including nothing.** With the appearance fields empty the preview
 * shows what the dashboard itself falls back to today — `Misc.Sparkle` and a Beta badge — and says
 * that is where it came from, so "I left it blank" and "I picked the sparkle" do not look alike.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import type { OptInAppearanceDraft } from '../../../Utils/optInFeatureForm';
import { IconGlyph } from '@/Shared/components/IconPicker/IconPicker';

/**
 * What the dashboard renders for a feature nobody styled.
 *
 * Hardcoded here on purpose and nowhere else: it is not a default this screen gets to choose, it
 * is a fact about the dashboard that the preview reports.
 */
const DASHBOARD_DEFAULT_ICON = 'Misc.Sparkle';
const DASHBOARD_DEFAULT_BADGE = 'Beta';

const wrapperStyle = css`
  display: flex;
  gap: var(--space-m);
  flex-wrap: wrap;
  max-width: 680px;
`;

const stateStyle = css`
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const stateLabelStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/** A slice of sidebar: the button sits at the top of it, above whatever the menu is. */
const sidebarStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  border: 1px solid var(--color-border);
`;

const buttonBase = `
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  width: 100%;
  padding: 8px 12px;
  border-radius: var(--radius-control);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-align: left;
`;

const primaryButtonStyle = css`
  ${buttonBase}
  background: var(--color-primary);
  color: var(--color-on-primary);
`;

const tertiaryButtonStyle = css`
  ${buttonBase}
  background: transparent;
  border: 1px solid var(--color-border-strong);
  color: var(--color-text);
`;

/** No ellipsis on purpose: a label that does not fit should look wrong here. */
const labelStyle = css`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const badgeStyle = css`
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: var(--radius-round);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 9px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
`;

/** The rows below the button, so the button reads as sitting above a menu and not inside one. */
const menuStubStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: 0.4;
`;

const menuRowStyle = css`
  height: 8px;
  border-radius: var(--radius-sm);
  background: var(--color-border-strong);
`;

const captionStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const descriptionStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const missingStyle = css`
  font-style: italic;
  font-weight: var(--font-weight-regular);
  color: var(--color-danger);
`;

/** What the OFF side becomes when the invitation was switched off. */
const noInviteStyle = css`
  padding: var(--space-m);
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-control);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

/**
 * A text nobody wrote, shown as a gap.
 *
 * The dashboard has no fallback of its own — an empty field renders as nothing — so the preview
 * marks it instead of quietly borrowing the English.
 */
const Text = ({ value, fallback }: { value: string; fallback: string }): JSX.Element =>
  value.trim() !== '' ? <span>{value}</span> : <span className={missingStyle}>{fallback}</span>;

/** The badge's own words, or the dashboard's, or nothing at all. */
const badgeLabel = (badge: string | null): string | null => {
  if (badge === null) return DASHBOARD_DEFAULT_BADGE;
  if (badge === 'NONE') return null;

  return badge === 'BETA' ? 'Beta' : 'New';
};

export interface OptInEntryPreviewProps {
  name: string;
  description: string;
  enterCta: string;
  exitCta: string;
  /** Already in the language of the open tab; it is the one appearance field that has one. */
  badgeTooltip: string;
  appearance: OptInAppearanceDraft;
}

export const OptInEntryPreview = ({
  name,
  description,
  enterCta,
  exitCta,
  badgeTooltip,
  appearance,
}: OptInEntryPreviewProps): JSX.Element => {
  const enterBadge = badgeLabel(appearance.enterBadge);
  const exitBadge = badgeLabel(appearance.exitBadge);
  const tooltip = badgeTooltip.trim() === '' ? undefined : badgeTooltip;

  const inherited = [
    appearance.enterIcon,
    appearance.exitIcon,
    appearance.enterBadge,
    appearance.exitBadge,
  ].some(value => value === null);

  return (
    <div id='opt-in-feature-preview-entry' className={wrapperStyle}>
      <div className={stateStyle}>
        <span className={stateLabelStyle}>Desligada — o convite</span>
        <div id='opt-in-feature-preview-entry-off' className={sidebarStyle}>
          {appearance.enterVisible ? (
            <div className={primaryButtonStyle}>
              <IconGlyph
                value={appearance.enterIcon ?? DASHBOARD_DEFAULT_ICON}
                size={16}
                color='var(--color-on-primary)'
              />
              <span className={labelStyle}>
                <Text value={enterCta} fallback='sem rótulo' />
              </span>
              {enterBadge === null ? null : (
                <span className={badgeStyle} title={tooltip}>
                  {enterBadge}
                </span>
              )}
            </div>
          ) : (
            <div id='opt-in-feature-preview-entry-hidden' className={noInviteStyle}>
              Sem convite: o botão não aparece para quem ainda não entrou. A funcionalidade continua
              ativa e quem já está dentro continua vendo o botão de voltar, ao lado.
            </div>
          )}

          <div className={menuStubStyle}>
            <div className={menuRowStyle} />
            <div className={menuRowStyle} />
            <div className={menuRowStyle} />
          </div>

          <span className={descriptionStyle}>
            <Text value={name} fallback='sem nome' /> —{' '}
            <Text value={description} fallback='sem descrição' />
          </span>
        </div>
      </div>

      <div className={stateStyle}>
        <span className={stateLabelStyle}>Ligada — mesmo botão, a volta</span>
        <div id='opt-in-feature-preview-entry-on' className={sidebarStyle}>
          <div className={tertiaryButtonStyle}>
            <IconGlyph
              value={appearance.exitIcon ?? DASHBOARD_DEFAULT_ICON}
              size={16}
              color='var(--color-text)'
            />
            <span className={labelStyle}>
              <Text value={exitCta} fallback='sem rótulo' />
            </span>
            {exitBadge === null ? null : (
              <span className={badgeStyle} title={tooltip}>
                {exitBadge}
              </span>
            )}
          </div>

          <div className={menuStubStyle}>
            <div className={menuRowStyle} />
            <div className={menuRowStyle} />
            <div className={menuRowStyle} />
          </div>

          <span className={descriptionStyle}>
            <Text value={name} fallback='sem nome' /> — já está usando a funcionalidade.
          </span>
        </div>
      </div>

      {inherited ? (
        <span id='opt-in-feature-preview-entry-inherited' className={captionStyle}>
          O que está vazio aparece aqui como o dashboard desenha hoje ({DASHBOARD_DEFAULT_ICON} e o
          selo Beta). Deixar vazio não muda nada na tela do cliente; escolher, sim.
        </span>
      ) : null}
    </div>
  );
};
